import * as React from 'react';
import { LocalDate, LocalDateTime, LocalTime } from 'js-joda';
import { isNil } from 'es-toolkit/compat';

import { DisplayDate, DisplaySchedule, DisplayTime } from '@tock/time';
import { DisplayFees } from 'components/business/offering/DisplayFees';
import { IconCalendar, IconCash, IconClock, IconInvoice, IconMapMarker } from '@tock/icons';
import { OfferingDetailSummaryRow } from 'components/offering-detail-page/OfferingDetailSummaryRow';
import { OfferingPriceDescriptionDisplay } from 'components/business/offering/OfferingPriceDescriptionDisplay';
import {
  PriceRange,
  getIsRequiredSupplementZero,
  getPriceDistribution,
} from 'components/business/PriceRange';
import { consumer, core } from '@tock/coretypes';
import { isPaymentCardHoldExperience, isZero } from 'utils/priceUtils';
import AdditionalCosts from 'components/AdditionalCosts';
import Address from 'components/business/location/Address';
import PureBaseComponent from '@tock/shared/components/PureBaseComponent';

interface Props {
  schedule?: core.Schedule;
  eventFirstDatetime?: LocalDateTime;
  eventLastDatetime?: LocalDateTime;
  startDate?: LocalDate;
  startTime?: LocalTime;
  endTime?: LocalTime;
  price?: consumer.IOfferedPriceSummary;
  location?: consumer.ILocation;
  shouldDisplayAddress: boolean;
  isAtOneTime: boolean;
  isOnOneDate: boolean;
  offeringType: consumer.Offering.Type;
}

export default class OfferingDetailSummary extends PureBaseComponent<Props> {
  getDateDisplay() {
    const { eventFirstDatetime, eventLastDatetime, startDate } = this.props;
    if (
      eventFirstDatetime &&
      eventLastDatetime &&
      !eventFirstDatetime.toLocalDate().isEqual(eventLastDatetime.toLocalDate())
    ) {
      return (
        <>
          <DisplayDate displayMonthAs="short" dateValue={eventFirstDatetime} /> -{' '}
          <DisplayDate displayMonthAs="short" dateValue={eventLastDatetime} />
        </>
      );
    }

    if (startDate) {
      return <DisplayDate showWeekday displayMonthAs={'long'} dateValue={startDate} />;
    }

    if (eventFirstDatetime) {
      return <DisplayDate showWeekday displayMonthAs={'long'} dateValue={eventFirstDatetime} />;
    }

    return null;
  }

  getSingleDateTimeDisplay(): React.ReactNode {
    const { eventFirstDatetime, startTime, endTime, isAtOneTime } = this.props;
    const startTimeToUse = startTime
      ? startTime
      : eventFirstDatetime
        ? eventFirstDatetime.toLocalTime()
        : undefined;

    if (startTimeToUse) {
      return (
        <>
          {!isAtOneTime && <div>Multiple times, starting at</div>}
          <DisplayTime timeValue={startTimeToUse} />
          {!isNil(endTime) && (
            <span>
              {' '}
              - <DisplayTime timeValue={endTime} />
            </span>
          )}
        </>
      );
    }
    return null;
  }

  getMultiDateTemplateTimeDisplay(): React.ReactNode {
    const { isAtOneTime, startTime, endTime } = this.props;
    if (isAtOneTime) {
      return (
        <>
          <DisplayTime timeValue={startTime} />
          {!isNil(endTime) && (
            <span>
              {' '}
              - <DisplayTime timeValue={endTime} />
            </span>
          )}
        </>
      );
    }
    return <>Multiple times</>;
  }

  getPerTicketPriceDisplay(): React.ReactNode {
    const { price, offeringType } = this.props;

    const isCreditCardHold = isPaymentCardHoldExperience({ price });

    if (!price?.pricePerPerson || isCreditCardHold) {
      return null;
    }

    // When the base ticket is free but a required fee applies, the fee line
    // already conveys the cost. Suppress the misleading "Free" price line so we
    // show one or the other, never "Free" alongside a required fee.
    const wouldRenderFree = isZero(price.pricePerPerson) && getIsRequiredSupplementZero(price);
    const hasRequiredFees =
      AdditionalCosts.hasPriceRanges(price as consumer.OfferedPriceSummary) ||
      (price.fees?.length ?? 0) > 0;

    if (wouldRenderFree && hasRequiredFees) {
      return null;
    }

    return (
      <OfferingDetailSummaryRow icon={IconCash}>
        <PriceRange
          price={price as consumer.OfferedPriceSummary}
          type={offeringType}
          priceDistribution={getPriceDistribution(offeringType, price)}
        />
      </OfferingDetailSummaryRow>
    );
  }

  getMinRequiredSpendDisplay(): React.ReactNode {
    const { price } = this.props;

    if (!price || (price?.minRequiredSpend?.totalCents ?? 0) === 0 || !price?.priceDescription) {
      return null;
    }

    return (
      <OfferingDetailSummaryRow icon={IconCash}>
        <OfferingPriceDescriptionDisplay
          minRequiredSpendCents={price.minRequiredSpend?.totalCents ?? undefined}
          priceDescription={price.priceDescription}
          priceVariability={price.minRequiredSpend?.variability ?? undefined}
        />
      </OfferingDetailSummaryRow>
    );
  }

  getRequiredFeesDisplay(): React.ReactNode {
    const { price } = this.props;
    if (!price || !AdditionalCosts.hasPriceRanges(price as consumer.OfferedPriceSummary)) {
      return null;
    }
    return (
      <OfferingDetailSummaryRow icon={IconInvoice} data-testid="required-fee-value-testid">
        <AdditionalCosts price={price as consumer.OfferedPriceSummary} />
      </OfferingDetailSummaryRow>
    );
  }

  getFeesDisplay(): React.ReactNode {
    const { price } = this.props;
    return (
      <OfferingDetailSummaryRow icon={IconInvoice} data-testid="display-fee-value-testid">
        <DisplayFees fees={price?.fees ?? []} />
      </OfferingDetailSummaryRow>
    );
  }

  render() {
    const {
      schedule,
      eventFirstDatetime,
      startDate,
      price,
      location,
      shouldDisplayAddress,
      isOnOneDate,
    } = this.props;

    if (!eventFirstDatetime && !startDate && !price && !location) {
      return null;
    }

    const dateDisplay = this.getDateDisplay();
    const timeDisplay = isOnOneDate
      ? this.getSingleDateTimeDisplay()
      : this.getMultiDateTemplateTimeDisplay();

    return (
      <div className="EventDetailSummary">
        {!isNil(schedule) && (
          <OfferingDetailSummaryRow icon={IconCalendar} alignTop>
            <DisplaySchedule schedule={schedule} filterDatesEarlierThan={LocalDate.now()} />
          </OfferingDetailSummaryRow>
        )}
        {isNil(schedule) && !isNil(dateDisplay) && (
          <OfferingDetailSummaryRow icon={IconCalendar}>{dateDisplay}</OfferingDetailSummaryRow>
        )}
        {timeDisplay && (
          <OfferingDetailSummaryRow icon={IconClock}>{timeDisplay}</OfferingDetailSummaryRow>
        )}
        {price?.priceDisplayStyle === 'MIN_REQUIRED_SPEND'
          ? this.getMinRequiredSpendDisplay()
          : this.getPerTicketPriceDisplay()}
        {price?.priceDisplayStyle === 'BASE_TICKET_PRICE' && this.getRequiredFeesDisplay()}
        {price?.priceDisplayStyle === 'ITEMIZED' &&
          price?.fees?.length !== 0 &&
          this.getFeesDisplay()}
        {location && shouldDisplayAddress && (
          <OfferingDetailSummaryRow icon={IconMapMarker} alignTop>
            <Address
              locationName={location?.name || undefined}
              address={location?.address || undefined}
              address2={location?.address2 || undefined}
              city={location?.city || undefined}
              state={location?.state || undefined}
              zipCode={location?.zipCode || undefined}
            />
          </OfferingDetailSummaryRow>
        )}
      </div>
    );
  }
}
